/**
 * <SubscriptionCard /> — Dashboard tile for premium subscribers showing their
 * current plan, next-renewal note, "Manage billing" (Stripe portal) and
 * "Cancel" (with a feedback modal that captures why they're leaving).
 */
import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, ExternalLink, X, Loader2 } from "lucide-react";

export default function SubscriptionCard() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCancel, setShowCancel] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await http.get("/premium/me");
      setMe(data);
    } catch { setMe(null); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setBusy(true);
    try {
      const { data } = await http.post("/premium/portal");
      if (data?.portal_url) window.location.href = data.portal_url;
      else toast.error("Couldn't open billing portal.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't open billing portal.");
    } finally { setBusy(false); }
  };

  if (loading || !me?.is_premium) return null;

  return (
    <>
      <div
        data-testid="subscription-card"
        className="wofy-card border-2"
        style={{ borderColor: "var(--wofy-primary)", background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)" }}
      >
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full flex items-center justify-center shrink-0" style={{ background: "var(--wofy-primary)" }}>
            <Sparkles className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="overline mb-1" style={{ color: "var(--wofy-primary)" }}>YOUR SUBSCRIPTION</div>
            <div className="font-display text-xl">{me.tier_name}</div>
            <div className="text-sm mt-1" style={{ color: "var(--wofy-text-soft)" }}>
              Billed {me.interval || "monthly"} · Status: <b>{me.status || "active"}</b>
              {me.cancel_at_period_end && <span className="ml-1 text-amber-700">(cancels at period end)</span>}
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              <button onClick={openPortal} disabled={busy} className="wofy-btn-ghost text-sm" data-testid="sub-manage-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                Manage billing
              </button>
              {!me.cancel_at_period_end && (
                <button onClick={() => setShowCancel(true)} className="wofy-btn-ghost text-sm text-red-600" data-testid="sub-cancel-btn">
                  Cancel subscription
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {showCancel && (
        <CancelModal
          me={me}
          onClose={() => setShowCancel(false)}
          onCancelled={() => { setShowCancel(false); load(); }}
        />
      )}
    </>
  );
}

const REASONS = [
  "Too expensive",
  "Not using it enough",
  "Missing a feature I needed",
  "Found a better alternative",
  "Temporary — I'll be back",
  "Other",
];

function CancelModal({ me, onClose, onCancelled }) {
  const [reason, setReason] = useState("");
  const [feedback, setFeedback] = useState("");
  const [wouldReturn, setWouldReturn] = useState(null);
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState(null);
  const [showFullForm, setShowFullForm] = useState(false);

  // Fetch retention offer on modal open — if the current tier has a
  // configured downgrade target, we'll show it before the cancel form.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await http.get("/premium/downgrade-offer");
        setOffer(data?.offer || null);
      } catch { /* no offer available — proceed to cancel form */ }
    })();
  }, []);

  const acceptDowngrade = async () => {
    if (!offer) return;
    setBusy(true);
    try {
      await http.post("/premium/downgrade", {
        target_tier_id: offer.target_tier_id,
        interval: offer.interval || "monthly",
      });
      toast.success(`Switched to ${offer.target_tier_name}. Thank you for staying!`);
      onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't switch plans.");
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await http.post("/premium/cancel", { reason, feedback, would_return: wouldReturn });
      toast.success("Cancelled — you'll keep access until the end of your billing period.");
      onCancelled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't cancel — try again shortly.");
    } finally { setBusy(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="cancel-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl p-6 space-y-4"
        style={{ background: "var(--wofy-bg)", border: "1px solid var(--wofy-border)" }}
      >
        <div className="flex items-center justify-between">
          <div>
            <div className="overline" style={{ color: "var(--wofy-primary)" }}>WE'LL MISS YOU</div>
            <h2 className="font-display text-2xl mt-1">Cancel {me.tier_name}?</h2>
          </div>
          <button type="button" onClick={onClose} className="wofy-btn-ghost" data-testid="cancel-modal-close"><X className="w-4 h-4" /></button>
        </div>

        {/* Save offer — shown BEFORE the cancel form. User can accept it and
            stay, or click "No thanks" to see the full cancel form. */}
        {offer && !showFullForm && (
          <div
            className="rounded-xl p-4 border-2"
            style={{ borderColor: "var(--wofy-primary)", background: "linear-gradient(135deg, #FEF3C7 0%, #FFE4E6 100%)" }}
            data-testid="downgrade-offer"
          >
            <div className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: "var(--wofy-primary)" }}>
              Before you go — one option
            </div>
            <div className="font-display text-lg">
              Stay on <b>{offer.target_tier_name}</b> for <b data-testid="downgrade-price">{offer.target_price || "less"}</b>
            </div>
            {offer.copy && (
              <p className="text-sm mt-1" style={{ color: "var(--wofy-text)" }}>{offer.copy}</p>
            )}
            {(offer.perks || []).length > 0 && (
              <ul className="text-xs mt-2 space-y-1">
                {offer.perks.slice(0, 4).map((p, i) => <li key={i}>• {p}</li>)}
              </ul>
            )}
            <div className="flex justify-end gap-2 mt-3">
              <button
                type="button"
                onClick={() => setShowFullForm(true)}
                className="wofy-btn-ghost text-xs"
                data-testid="offer-decline"
              >
                No thanks, cancel anyway
              </button>
              <button
                type="button"
                onClick={acceptDowngrade}
                disabled={busy}
                className="wofy-btn-primary text-sm"
                data-testid="offer-accept"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Stay for {offer.target_price || "less"}
              </button>
            </div>
          </div>
        )}

        {/* Full cancel form — always available if no offer or user declined it. */}
        {(!offer || showFullForm) && (
          <>
            <p className="text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Your subscription will end at the close of the current billing period. You'll keep every premium feature until then. A single question would help us a lot:
            </p>

            <div>
              <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>What's the main reason?</span>
              <div className="grid gap-1 mt-2">
                {REASONS.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="cancel-reason" value={r} checked={reason === r} onChange={(e) => setReason(e.target.value)} data-testid={`cancel-reason-${r.slice(0,10)}`} />
                    {r}
                  </label>
                ))}
              </div>
            </div>

            <label className="block">
              <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Anything you'd like us to fix? (optional)</span>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                rows={3}
                className="wofy-input mt-2"
                placeholder="A missing feature, pricing, something we broke…"
                data-testid="cancel-feedback"
                maxLength={2000}
              />
            </label>

            <div>
              <span className="text-xs font-mono uppercase tracking-wider" style={{ color: "var(--wofy-text-soft)" }}>Would you consider coming back?</span>
              <div className="flex gap-4 mt-2 text-sm">
                {[
                  { v: true, label: "Yes, someday" },
                  { v: false, label: "Probably not" },
                ].map((o) => (
                  <label key={String(o.v)} className="inline-flex items-center gap-2 cursor-pointer">
                    <input type="radio" name="cancel-return" checked={wouldReturn === o.v} onChange={() => setWouldReturn(o.v)} data-testid={`cancel-return-${o.v}`} />
                    {o.label}
                  </label>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t" style={{ borderColor: "var(--wofy-line)" }}>
              <button type="button" onClick={onClose} className="wofy-btn-ghost">Keep my plan</button>
              <button type="submit" className="wofy-btn-primary bg-red-600" disabled={busy} data-testid="cancel-confirm">
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Confirm cancellation
              </button>
            </div>
          </>
        )}
      </form>
    </div>
  );
}
